/**
 * Landing Page Navigation Constants
 *
 * Rich dropdown menu structure matching the reference design.
 * Uses CDN images for dropdown cards.
 *
 * IMPORTANT: This file must NOT import lucide-react or any heavy icon library.
 * It is imported by landing pages where bundle size is critical.
 * App-specific nav config lives in navigation-app.ts.
 */

import { getTestimonialsForNav } from "@/utils/clients"

export const NAV_ITEMS = {
    platform: {
        main: [
            {
                title: "Growth",
                description: "Our AI engages with your target audience automatically to drive real organic growth.",
                image: "/img/nav/nav-growth.avif",
                href: "/instagram-growth-service"
            },
            {
                title: "Analytics",
                description: "Monitor your daily follower growth, engagement rates, and account health in real-time.",
                image: "/img/nav/nav-analytics.avif",
                href: "/chatgpt-for-instagram"
            },
            {
                title: "Content",
                description: "Plan and schedule your posts to keep your feed active while we handle the growth.",
                image: "/img/nav/nav-content.avif",
                href: "/instagram-bot"
            },
            {
                title: "Search Influencers",
                description: "Discover creators by niche, location, audience size, and engagement rate.",
                image: "/img/nav/nav-search-influencers.avif",
                href: "/find-influencers"
            }
        ],
        integrations: [
            {
                title: "Instagram",
                description: "Primary support for automated growth, engagement, and targeting.",
                icon: "/img/nav/icon-instagram.svg",
                href: "#"
            },
            {
                title: "TikTok",
                description: "Grow your TikTok presence using the same powerful AI targeting strategies.",
                icon: "/img/nav/icon-tiktok.svg",
                href: "#"
            }
        ],
        more: [
            { title: "Product tour", icon: "/img/nav/icon-product-tour.png", href: "#" },
            { title: "Compare Plans", icon: "/img/outfame-logomark-black.svg", href: "/pricing" },
            { title: "Case Studies", icon: "/img/nav/icon-case-studies.avif", href: "/results" },
            { title: "FAQ", icon: "/img/nav/icon-faq.png", href: "/pricing#faq" }
        ]
    },
    resources: {
        main: [
            {
                title: "Affiliate Program",
                description: "Earn 40% lifetime commission on every referral you bring to Outfame.",
                image: "/img/nav/nav-affiliate-program.avif",
                href: "/affiliate-program"
            },
            {
                title: "Growth Newsletter",
                description: "Weekly tips on Instagram growth and algorithm hacks.",
                image: "/img/nav/nav-newsletter.avif",
                href: "#"
            },
            {
                title: "Blog",
                description: "Tips, guides, and case studies",
                image: "/img/nav/nav-blog.avif",
                href: "/blog"
            },
            {
                title: "Outfame Results",
                description: "Real success stories and growth results from Outfame creators.",
                image: "/img/nav/nav-outfame-results.avif",
                href: "/outfame-reviews"
            }
        ],
        more: [
            { title: "Free Instagram Tools", icon: "/img/nav/icon-tools.png", href: "/free-instagram-tools" },
            { title: "Help Center", icon: "/img/nav/icon-faq.png", href: "https://support.outfame.com/en/" },
            { title: "Contact Us", icon: "/img/nav/icon-mail.png", href: "/contact" },
            { title: "FAQ", icon: "/img/nav/icon-faq.png", href: "/pricing#faq" },
            { title: "About", icon: "/img/outfame-logomark-black.svg", href: "/about" }
        ]
    }
}

export const FOOTER_LINKS = {
    company: [
        { key: "blog", label: "Blog", href: "/blog" },
        { key: "affiliate", label: "Affiliate Program", mobileLabel: "Affiliate", href: "/affiliate-program", badge: "Earn 40%" },
        // TODO: Create /newsletter page - email signup with weekly growth tips
        { key: "newsletter", label: "ROI Newsletter", href: "#" },
        { key: "switch", label: "Switch to Outfame", href: "/switch-to-outfame" },
        { key: "about", label: "About Us", mobileLabel: "About", href: "/about" },
        { key: "contact", label: "Contact Us", mobileLabel: "Contact", href: "/contact" },
        { key: "start", label: "Get Started", href: "/start" },
        { key: "help", label: "Help Center", href: "https://support.outfame.com/en/" },
        { key: "careers", label: "Careers", href: "/careers", badge: "Hiring" },
        // TODO: Create /data page - transparency page showing data sources and methodology
        { key: "data", label: "Our Data", href: "#" }
    ],
    services: [
        { key: "buy-followers", label: "Buy Instagram Followers", href: "/buy-instagram-followers" },
        { key: "growth-service", label: "Instagram Growth Service", href: "/instagram-growth-service" },
        { key: "free-followers", label: "Free Instagram Followers", href: "/free-instagram-followers" },
        { key: "buy-likes", label: "Buy Instagram Likes", href: "/buy-instagram-likes" },
        { key: "buy-views", label: "Buy Instagram Views", href: "/buy-instagram-views" },
    ],
    resources: [
        { key: "how-it-works-resource", label: "How It Works", href: "/how-it-works" },
        { key: "reviews", label: "Outfame Reviews", href: "/outfame-reviews" },
        { key: "influencer-guide", label: "Influencer Marketing Guide", href: "/influencer-marketing-guide" },
        { key: "results", label: "Growth Results", href: "/results" },
        { key: "pricing-resource", label: "Pricing", href: "/pricing" },
        { key: "growth-service-cost", label: "Instagram growth service cost", href: "/instagram-growth-service-cost" },
        { key: "is-legit", label: "Is Outfame Legit?", href: "/blog/is-outfame-legit" },
    ],
    compare: [
        { key: "path-social", label: "Path Social Alternative", href: "/path-social-alternative" },
        { key: "nitreo", label: "Nitreo Alternative", href: "/nitreo-alternative" },
        { key: "kicksta", label: "Kicksta Alternative", href: "/kicksta-alternative" },
        { key: "plixi", label: "Plixi Alternative", href: "/plixi-alternative" },
        { key: "ampfluence", label: "Ampfluence Alternative", href: "/ampfluence-alternative" },
        { key: "upleap", label: "Upleap Alternative", href: "/upleap-alternative" },
        { key: "best-agencies", label: "Best Instagram growth agencies", href: "/best-instagram-growth-agencies" },
        { key: "view-all-compare", label: "View all comparisons", href: "/instagram-growth-comparison", isViewAll: true }
    ],
    features: [
        { key: "targeting", label: "Instagram Targeting", href: "/instagram-targeting" },
        { key: "safe-growth", label: "Safe Instagram Growth", href: "/safe-instagram-growth" },
        { key: "real-followers", label: "Real Instagram Followers", href: "/real-instagram-followers" },
        { key: "analytics", label: "Instagram Analytics", href: "/instagram-analytics" },
        { key: "automation", label: "Instagram Automation", href: "/instagram-automation" }
    ],
    tools: [
        { key: "fake-check", label: "Fake Follower Check", href: "/free-instagram-tools/instagram-audit" },
        { key: "engagement-calc", label: "Engagement Calculator", href: "/free-instagram-tools/instagram-engagement-calculator" },
        { key: "influencer-search", label: "Influencer Search", href: "/free-instagram-tools/instagram-influencer-search" },
        { key: "profile-analyzer", label: "Profile Analyzer", href: "/free-instagram-tools/instagram-audit" },
        { key: "hashtag-generator", label: "Hashtag Generator", href: "/free-instagram-tools/instagram-hashtag-generator" },
        { key: "ig-analytics", label: "Instagram Analytics Checker", href: "/instagram" },
        { key: "all-tools", label: "All Free Instagram Tools", href: "/free-instagram-tools", isViewAll: true }
    ],
    social: [
        { label: "Instagram", href: "https://www.instagram.com/outfame.app", icon: "instagram" },
        { label: "X", href: "https://x.com/OutfameApp", icon: "x" },
        { label: "LinkedIn", href: "https://www.linkedin.com/company/outfame", icon: "linkedin" },
        { label: "YouTube", href: "https://www.youtube.com/@Outfame-com", icon: "youtube" },
        { label: "TikTok", href: "https://www.tiktok.com/@outfame.com", icon: "tiktok" }
    ],
    legal: [
        { key: "terms", label: "Terms & Service", href: "/terms-of-service" },
        { key: "privacy", label: "Privacy Policy", href: "/privacy" }
    ],
    locations: [
        { key: "la", city: "Los Angeles, US", address: "2049 Century Park E\nLos Angeles, CA 90067" },
        { key: "sg", city: "Singapore, SG", address: "1 Raffles Place, Tower 1\nSingapore 048616" }
    ]
}

// Testimonials data from centralized client personas
export const TESTIMONIALS = getTestimonialsForNav()
